import React from "react";
import { ActivityCategory } from "../types";
import { fallbackImage } from "../utils/getCachedImage";
import { getCategoryImageMap } from "./remoteConfig";

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return hash;
};

// Category-based default images for better visual experience
export const getCategoryDefaultImage = (
  category: ActivityCategory,
  seed: string,
): string => {
  const mappedUrl = getCategoryImageMap()?.[category];
  if (Array.isArray(mappedUrl) && mappedUrl.length > 0) {
    const cleaned = mappedUrl
      .map((u) => (typeof u === "string" ? u.trim() : ""))
      .filter(Boolean);
    if (cleaned.length > 0) {
      const deterministicSeed = seed.trim() || category;
      const index = Math.abs(hashString(deterministicSeed)) % cleaned.length;
      return cleaned[index];
    }
  }
  // If remote config has no value, fall back to the generic cached image
  return fallbackImage;
};

// Clean title by removing extra whitespace and HTML entities
export const cleanTitle = (title: string): string => {
  return title
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

// Get image source with fallback to category default
export const getActivityImageSource = (activity: {
  imageUrl?: string;
  category: ActivityCategory;
  id?: string;
  title?: string;
}): string => {
  if (activity.imageUrl) return activity.imageUrl;
  const seed = activity.id || activity.title || activity.category;
  return getCategoryDefaultImage(activity.category, seed);
};

// Enhanced image error handler with category fallback
export const createImageErrorHandler = (activity: {
  category: ActivityCategory;
  id?: string;
  title?: string;
}) => {
  return (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.currentTarget;
    const currentFallback = img.getAttribute("data-fallback-level") || "0";

    // First try category-specific default image
    if (currentFallback === "0") {
      img.setAttribute("data-fallback-level", "1");
      const seed = activity.id || activity.title || activity.category;
      img.src = getCategoryDefaultImage(activity.category, seed);
      return;
    }

    // If category image also fails, use basic fallback
    if (currentFallback === "1") {
      img.setAttribute("data-fallback-level", "2");
      img.src = fallbackImage;
    }

    // Stop here - no more fallbacks to prevent infinite loops
  };
};
